import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Row,
  Col,
  Button,
  Space,
  message,
  Switch,
  InputNumber,
  Typography,
} from 'antd';
import {
  UserOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { apiService, Client, Coach } from '../../../../services/api';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

interface EditClientModalProps {
  visible: boolean;
  client: Client | null;
  onCancel: () => void;
  onSuccess: (updatedClient: Client) => void;
}

interface EditClientFormData {
  // Basic information (read-only for existing clients)
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  
  // Editable client information
  tier: 'BASIC' | 'STANDARD' | 'PREMIUM' | 'ELITE';
  subscriptionType?: 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
  agreedPrice?: number;
  assignedCoachId?: string;
  
  // Services
  services: {
    training: boolean;
    nutrition: boolean;
    habitCoaching: boolean;
  };
  
  // Goals and health
  goals: string[];
  healthConditions: string[];
  
  // Emergency contact
  emergencyContact?: {
    name: string;
    phone: string;
    relationship: string;
  };
  
  // Notes
  notes?: string;
  
  // Status
  isActive: boolean;
}

const EditClientModal: React.FC<EditClientModalProps> = ({
  visible,
  client,
  onCancel,
  onSuccess,
}) => {
  console.log('🔄 EditClientModal - Component rendered with props:', {
    visible,
    client: client ? { id: client.id, services: client.services } : null
  });

  const [form] = Form.useForm<EditClientFormData>();
  const [loading, setLoading] = useState(false);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialValues, setInitialValues] = useState<EditClientFormData | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Fetch coaches for assignment
  useEffect(() => {
    const fetchCoaches = async () => {
      try {
        const response = await apiService.getCoaches({ isActive: true });
        if (response.success && response.data) {
          setCoaches(response.data.data || []);
        }
      } catch (error) {
        console.error('Failed to fetch coaches:', error);
      }
    };

    if (visible) {
      fetchCoaches();
    }
  }, [visible]);

  // Debug client prop changes
  useEffect(() => {
    console.log('🔄 EditClientModal - Client prop changed:', client);
    if (client) {
      console.log('🔄 EditClientModal - Client services:', client.services);
      console.log('🔄 EditClientModal - Full client object:', JSON.stringify(client, null, 2));
    }
  }, [client]);

  // Populate form when client data is available
  useEffect(() => {
    console.log('🔄 EditClient - Form population useEffect triggered:', {
      client: !!client,
      visible,
      clientId: client?.id,
      services: client?.services
    });

    if (client && visible) {
      console.log('🔄 EditClient - Client data received:', client);
      console.log('🔄 EditClient - Client services from backend:', client.services);
      
      const formData: EditClientFormData = {
        firstName: client.user?.firstName || '',
        lastName: client.user?.lastName || '',
        email: client.user?.email || '',
        phoneNumber: client.user?.phoneNumber || '',
        tier: client.tier,
        subscriptionType: client.subscriptionType || undefined,
        agreedPrice: client.agreedPrice || undefined,
        assignedCoachId: client.assignedCoachId || undefined,
        services: client.services || {
          training: false,
          nutrition: false,
          habitCoaching: false,
        },
        goals: client.goals || [],
        healthConditions: client.healthConditions || [],
        emergencyContact: client.emergencyContact || undefined,
        notes: client.notes || '',
        isActive: client.isActive,
      };
      
      console.log('🔄 EditClient - Prepared form data:', formData);
      console.log('🔄 EditClient - Services in form data:', formData.services);
      
      form.setFieldsValue(formData);
      
      // Debug what was actually set in the form
      setTimeout(() => {
        const setValues = form.getFieldsValue();
        console.log('🔄 EditClient - Form values after setFieldsValue:', setValues);
        console.log('🔄 EditClient - Services in form after setting:', setValues.services);
        console.log('🔄 EditClient - Training field value:', form.getFieldValue(['services', 'training']));
        console.log('🔄 EditClient - Nutrition field value:', form.getFieldValue(['services', 'nutrition']));
        console.log('🔄 EditClient - Habit coaching field value:', form.getFieldValue(['services', 'habitCoaching']));
      }, 100);
      
      setInitialValues(formData); // Store initial values
      setHasChanges(false); // Reset change state when new data is loaded
    } else if (!visible) {
      console.log('🔄 EditClient - Modal not visible, resetting states');
      // Reset states when modal is closed
      setHasChanges(false);
      setInitialValues(null);
      setShowCancelConfirm(false); // Reset confirmation modal state
    } else {
      console.log('🔄 EditClient - Form population conditions not met:', {
        hasClient: !!client,
        visible
      });
    }
  }, [client, visible, form]);

  const handleSubmit = async () => {
    if (!client) return;

    try {
      const values = await form.validateFields();
      setLoading(true);

      console.log('Raw form values before processing:', values);

      // Ensure services object is complete
      const services = {
        training: values.services?.training || false,
        nutrition: values.services?.nutrition || false,
        habitCoaching: values.services?.habitCoaching || false,
      };

      console.log('Processed services:', services);
      console.log('EditClient - Services validation check:');
      console.log('  - services object exists:', !!values.services);
      console.log('  - training:', values.services?.training);
      console.log('  - nutrition:', values.services?.nutrition);
      console.log('  - habitCoaching:', values.services?.habitCoaching);

      // Prepare update data (only include fields that can be updated)
      // Note: firstName, lastName, email, phoneNumber are read-only for existing clients
      const updateData = {
        tier: values.tier,
        subscriptionType: values.subscriptionType || undefined,
        agreedPrice: values.agreedPrice ? Number(values.agreedPrice) : undefined,
        assignedCoachId: values.assignedCoachId || undefined,
        services: services,
        goals: values.goals || [],
        healthConditions: values.healthConditions || [],
        emergencyContact: (values.emergencyContact?.name || values.emergencyContact?.phone || values.emergencyContact?.relationship) 
          ? {
              name: values.emergencyContact?.name || '',
              phone: values.emergencyContact?.phone || '',
              relationship: values.emergencyContact?.relationship || '',
            }
          : undefined,
        notes: values.notes || undefined,
        isActive: values.isActive,
      };

      console.log('Sending update data to API:', updateData);
      console.log('Client ID:', client.id);
      const response = await apiService.updateClient(client.id, updateData);
      
      if (response.success && response.data) {
        message.success('Client updated successfully!');
        onSuccess(response.data);
        onCancel();
      } else {
        throw new Error(response.error || 'Failed to update client');
      }
    } catch (error: any) {
      console.error('Failed to update client:', error);
      
      // Handle API validation errors
      if (error.response?.data?.details) {
        const validationErrors = error.response.data.details;
        
        // Set form field errors
        const fieldErrors = validationErrors.map((err: any) => ({
          name: err.field.split('.'),
          errors: [err.message],
        }));
        
        form.setFields(fieldErrors);
        
        const errorMessages = validationErrors.map((err: any) => `${err.field}: ${err.message}`).join(', ');
        message.error(`Validation failed: ${errorMessages}`);
      } else {
        message.error(error.message || 'Failed to update client');
      }
    } finally {
      setLoading(false);
    }
  };

  // Check if form has any unsaved changes
  const detectChanges = () => {
    try {
      if (!client || !initialValues) {
        setHasChanges(false);
        return false;
      }
      
      const currentValues = form.getFieldsValue();
      
      // Compare all editable fields
      const tierChanged = currentValues.tier !== initialValues.tier;
      const subscriptionTypeChanged = currentValues.subscriptionType !== initialValues.subscriptionType;
      const agreedPriceChanged = Number(currentValues.agreedPrice || 0) !== Number(initialValues.agreedPrice || 0);
      const assignedCoachChanged = currentValues.assignedCoachId !== initialValues.assignedCoachId;
      
      // Compare services
      const servicesChanged = 
        currentValues.services?.training !== initialValues.services?.training ||
        currentValues.services?.nutrition !== initialValues.services?.nutrition ||
        currentValues.services?.habitCoaching !== initialValues.services?.habitCoaching;
      
      // Compare arrays (goals and health conditions)
      const goalsChanged = JSON.stringify(currentValues.goals || []) !== JSON.stringify(initialValues.goals || []);
      const healthConditionsChanged = JSON.stringify(currentValues.healthConditions || []) !== JSON.stringify(initialValues.healthConditions || []);
      
      // Compare emergency contact
      const emergencyContactChanged = 
        currentValues.emergencyContact?.name !== initialValues.emergencyContact?.name ||
        currentValues.emergencyContact?.phone !== initialValues.emergencyContact?.phone ||
        currentValues.emergencyContact?.relationship !== initialValues.emergencyContact?.relationship;
      
      // Compare notes and status
      const notesChanged = (currentValues.notes || '') !== (initialValues.notes || '');
      const statusChanged = currentValues.isActive !== initialValues.isActive;
      
      const hasFormChanges = tierChanged || subscriptionTypeChanged || agreedPriceChanged || 
                           assignedCoachChanged || servicesChanged || goalsChanged || 
                           healthConditionsChanged || emergencyContactChanged || 
                           notesChanged || statusChanged;
      
      console.log('🔍 Change detection:', {
        tierChanged,
        subscriptionTypeChanged,
        agreedPriceChanged,
        assignedCoachChanged,
        servicesChanged,
        goalsChanged,
        healthConditionsChanged,
        emergencyContactChanged,
        notesChanged,
        statusChanged,
        hasFormChanges
      });
      
      setHasChanges(hasFormChanges);
      return hasFormChanges;
    } catch (error) {
      console.error('Error detecting changes:', error);
      setHasChanges(false);
      return false;
    }
  };

  // Legacy function for compatibility
  const hasUnsavedChanges = () => {
    return hasChanges;
  };

  const handleCancel = () => {
    try {
      const hasChanges = hasUnsavedChanges();
      console.log('Edit cancel clicked - hasChanges:', hasChanges);
      
      if (hasChanges) {
        setShowCancelConfirm(true);
      } else {
        handleReset();
      }
    } catch (error) {
      console.error('Error in edit handleCancel:', error);
      // If there's an error, just close the modal
      handleReset();
    }
  };

  const handleReset = () => {
    form.resetFields();
    setHasChanges(false); // Reset change tracking
    onCancel();
  };

  if (!client) {
    return null;
  }

  return (
    <Modal
      title={
        <Space>
          <UserOutlined />
          Edit Client: {client.user?.firstName} {client.user?.lastName}
          {hasChanges && (
            <Text type="warning" style={{ fontSize: '12px', fontWeight: 'normal' }}>
              • Unsaved changes
            </Text>
          )}
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      width={800}
      footer={null}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={(changedValues, allValues) => {
          // Trigger change detection when any field changes
          setTimeout(() => detectChanges(), 100); // Small delay to ensure form values are updated
          
          // Debug services changes specifically
          if (changedValues.services) {
            console.log('🔄 EditClient - Services form values changed:', changedValues.services);
            console.log('🔄 EditClient - All services in form:', allValues.services);
          }
        }}
      >
        {/* Read-only Basic Information */}
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Text strong>Basic Information (Read-only)</Text>
          </Col>
          <Col span={12}>
            <Form.Item name="firstName" label="First Name">
              <Input disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="lastName" label="Last Name">
              <Input disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="email" label="Email">
              <Input disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="phoneNumber" label="Phone Number">
              <Input disabled placeholder="Phone number updates not supported yet" />
            </Form.Item>
          </Col>
        </Row>

        {/* Editable Service Information */}
        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          <Col span={24}>
            <Text strong>Service Configuration</Text>
          </Col>
          <Col span={12}>
            <Form.Item
              name="tier"
              label="Tier"
              rules={[{ required: true, message: 'Please select a tier' }]}
            >
              <Select placeholder="Select tier">
                <Option value="BASIC">Basic</Option>
                <Option value="STANDARD">Standard</Option>
                <Option value="PREMIUM">Premium</Option>
                <Option value="ELITE">Elite</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="subscriptionType" label="Subscription Type">
              <Select placeholder="Select subscription type" allowClear>
                <Option value="WEEKLY">Weekly</Option>
                <Option value="MONTHLY">Monthly</Option>
                <Option value="QUARTERLY">Quarterly</Option>
                <Option value="YEARLY">Yearly</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="agreedPrice" label="Agreed Price ($)">
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Enter agreed price"
                min={0}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="assignedCoachId" label="Assigned Coach">
              <Select placeholder="Select a coach" allowClear>
                {coaches.map(coach => (
                  <Option key={coach.id} value={coach.id}>
                    {coach.user ? `${coach.user.firstName} ${coach.user.lastName}` : 'Unknown Coach'}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Services</Text>
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Form.Item name={['services', 'training']} valuePropName="checked">
                  <Space>
                    <Switch 
                      checked={form.getFieldValue(['services', 'training'])}
                      onChange={(checked) => {
                        console.log('🏋️ EditClient - Training switch toggled:', checked);
                        // Force form field update
                        form.setFieldValue(['services', 'training'], checked);
                        console.log('🏋️ EditClient - Form training value after update:', form.getFieldValue(['services', 'training']));
                      }} 
                    />
                    <Text>Training</Text>
                  </Space>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name={['services', 'nutrition']} valuePropName="checked">
                  <Space>
                    <Switch 
                      checked={form.getFieldValue(['services', 'nutrition'])}
                      onChange={(checked) => {
                        console.log('🥗 EditClient - Nutrition switch toggled:', checked);
                        // Force form field update
                        form.setFieldValue(['services', 'nutrition'], checked);
                        console.log('🥗 EditClient - Form nutrition value after update:', form.getFieldValue(['services', 'nutrition']));
                      }} 
                    />
                    <Text>Nutrition</Text>
                  </Space>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name={['services', 'habitCoaching']} valuePropName="checked">
                  <Space>
                    <Switch 
                      checked={form.getFieldValue(['services', 'habitCoaching'])}
                      onChange={(checked) => {
                        console.log('🎯 EditClient - Habit Coaching switch toggled:', checked);
                        // Force form field update
                        form.setFieldValue(['services', 'habitCoaching'], checked);
                        console.log('🎯 EditClient - Form habit coaching value after update:', form.getFieldValue(['services', 'habitCoaching']));
                      }} 
                    />
                    <Text>Habit Coaching</Text>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Goals and Health */}
        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          <Col span={24}>
            <Text strong>Goals & Health Information</Text>
          </Col>
          <Col span={12}>
            <Form.Item name="goals" label="Goals">
              <Select mode="tags" placeholder="Enter goals (press Enter to add)">
                <Option value="weight_loss">Weight Loss</Option>
                <Option value="muscle_gain">Muscle Gain</Option>
                <Option value="strength">Strength Training</Option>
                <Option value="endurance">Endurance</Option>
                <Option value="flexibility">Flexibility</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="healthConditions" label="Health Conditions">
              <Select mode="tags" placeholder="Enter health conditions (press Enter to add)">
                <Option value="diabetes">Diabetes</Option>
                <Option value="hypertension">Hypertension</Option>
                <Option value="heart_disease">Heart Disease</Option>
                <Option value="arthritis">Arthritis</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Emergency Contact */}
        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          <Col span={24}>
            <Text strong>Emergency Contact</Text>
          </Col>
          <Col span={12}>
            <Form.Item 
              name={['emergencyContact', 'name']} 
              label="Contact Name"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const phone = getFieldValue(['emergencyContact', 'phone']);
                    const relationship = getFieldValue(['emergencyContact', 'relationship']);
                    
                    // If any emergency contact field is filled, name is required
                    if ((phone || relationship) && !value) {
                      return Promise.reject(new Error('Contact name is required when providing emergency contact'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input placeholder="Enter emergency contact name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item 
              name={['emergencyContact', 'phone']} 
              label="Contact Phone"
              rules={[
                {
                  pattern: /^\+?[1-9]\d{1,14}$/,
                  message: 'Enter a valid phone number (e.g., +14375051636). Must start with 1-9.',
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const name = getFieldValue(['emergencyContact', 'name']);
                    const relationship = getFieldValue(['emergencyContact', 'relationship']);
                    
                    // If any emergency contact field is filled, phone is required
                    if ((name || relationship) && !value) {
                      return Promise.reject(new Error('Contact phone is required when providing emergency contact'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
              extra="Format: +14375051636 or 14375051636 (must start with 1-9, not 0)"
            >
              <Input placeholder="Enter emergency contact phone (e.g., +14375051636)" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item 
              name={['emergencyContact', 'relationship']} 
              label="Relationship"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const name = getFieldValue(['emergencyContact', 'name']);
                    const phone = getFieldValue(['emergencyContact', 'phone']);
                    
                    // If any emergency contact field is filled, relationship is required
                    if ((name || phone) && !value) {
                      return Promise.reject(new Error('Relationship is required when providing emergency contact'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input placeholder="e.g., Spouse, Parent, Friend" />
            </Form.Item>
          </Col>
        </Row>

        {/* Notes and Status */}
        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          <Col span={24}>
            <Text strong>Additional Information</Text>
          </Col>
          <Col span={18}>
            <Form.Item name="notes" label="Notes">
              <TextArea
                rows={4}
                placeholder="Any additional notes about the client..."
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="isActive" label="Client Status" valuePropName="checked">
              <Space>
                <Switch />
                <Text>Active</Text>
              </Space>
            </Form.Item>
          </Col>
        </Row>

        {/* Form Actions */}
        <Row justify="end" style={{ marginTop: 24 }}>
          <Space>
            <Button onClick={handleCancel}>
              Cancel
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading} 
              icon={<SaveOutlined />}
              disabled={!hasChanges}
              title={hasChanges ? 'Save your changes' : 'No changes to save'}
            >
              {hasChanges ? 'Save Changes' : 'No Changes'}
            </Button>
          </Space>
        </Row>
      </Form>

      <Modal
        title="Confirm Cancel"
        open={showCancelConfirm}
        onOk={() => {
          handleReset();
          setShowCancelConfirm(false);
        }}
        onCancel={() => {
          setShowCancelConfirm(false);
        }}
        okText="Yes, discard changes"
        cancelText="No, keep editing"
        okType="danger"
      >
        <p>You have unsaved changes. All changes will be lost. Do you want to continue?</p>
      </Modal>
    </Modal>
  );
};

export default EditClientModal; 